import { generateBodyHtml } from './renderHtml'
import stylesGenerator from './styles/generator'
import {
  stripVariantConditions,
  asyncStripVariantConditions,
} from './helpers/stripVariantConditions'
import getJs from './helpers/renderer_eval_js'
import getClientSideJs from './helpers/renderer_eval_js_complete'
import getClientSideJsV3 from './helpers/renderer_eval_js_v3'
import loadExternalContent from './helpers/loadExternalContent'
import isBuilderEditingMode from './helpers/isBuilderEditingMode'
import notify, { MIGRATION_ISSUES_CHANNEL_ID } from './helpers/notifyBackend'
import { getStripeKey } from './payments-v2/stripeHelpers'
import { testModeIsOn } from './helpers/utils'

import type { Component, Context, Flow, FlowPage, FlowMetadata } from './models/types'

type Headers = any

type FirebaseResponseFlow = {
  fields: {
    form: { stringValue: string }
    groupId: { stringValue: string }
    isLive: { booleanValue: boolean }
    primaryProductId: { stringValue: string }
    template: { booleanValue: boolean }
    title: { stringValue: string }
    version: { integerValue: string }
    json_storage_location?: { stringValue: string }
  }
}

type FirebaseResponseLibraryItem = {
  fields: {
    body: { stringValue: string }
    group_id: { stringValue: string }
    is_public: { stringValue: string }
    key: { stringValue: string }
    name: { stringValue: string }
    object_type: { stringValue: string }
    type: { stringValue: string }
  }
}

declare const FLOWS_KV: {
  get(key: string): Promise<string>
  set(key: string, value: string): Promise<void>
}
// = {
//   get: async (key: string) => {
//     const response = await fetch(`https://flow-storage.heysavvy.workers.dev/flows/${key}`)
//     return response.text()
//   },
//   async set(key: string, value: string) {
//     await fetch(`https://flow-storage.heysavvy.workers.dev/flows/${key}`, {
//       method: 'POST',
//       body: value,
//     })

//     return
//   },
// }

const SUPABASE_DATABASE_URL = 'https://ierxexdtyashuotcsjyo.supabase.co'
const SUPABASE_PUBLIC_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImllcnhleGR0eWFzaHVvdGNzanlvIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NjQ4ODU2NDIsImV4cCI6MTk4MDQ2MTY0Mn0.AbUQz18lps79WZBW1kmbFP2lUTiAF6ttTuFK14yvUuw'

export async function handleRequest(
  context: Context,
  request: {
    url: URL
    method: string
    headers: Headers | null
    body: { json: any } | null
  }
): Promise<{ responseData: any; contentType?: string }> {
  const timeHandleRequest = Date.now()
  const logger = context.logger
  let flowId: string | undefined

  try {
    const host = request.headers?.get('Host')
    const referrer = request.headers?.get('Referer')
    const childLogger = logger?.child({
      host,
      referrer,
    })
    logger?.info({
      scope: 'Handle request',
      functionName: 'handleRequest',
      message: 'Request received',
      props: {
        requestUrlPathname: request.url
          ? { requestUrlPathname: request.url.pathname }
          : { _no_requestUrlPathname: true },
      },
    })
    // console.log(`📬 Request (${context.env})`, request, request.url.pathname, request.body)
    let forcedJson:
      | {
          repeatables: Component[]
          flow: Flow
          flowId: string
          groupId: string
          projectId: string
          page?: FlowPage
          component?: Component
        }
      | undefined = request.body && typeof request.body === 'object' && request.body.json

    const childLogger2 = childLogger?.child({
      forced_json: { ...forcedJson, flow: undefined, page: undefined },
    })
    childLogger2?.info({
      scope: 'Handle request',
      functionName: 'handleRequest',
      message: 'JSON extracted',
    })

    let origin: string =
      context.env === 'dev' || context.env === 'ci'
        ? 'http://localhost:8787'
        : (request.headers && request.headers.get('Host-Origin')) || request.url.origin

    // if (context.env === 'ci') origin = origin + '/api'

    const searchParams = request.url.searchParams

    const path = request.url.pathname.split('/').slice(1)
    const branchId = searchParams.get('embeddable_branch') || searchParams.get('branch')
    const versionQuery = searchParams.get('version') || searchParams.get('savvy_flow_version')
    //* We shouldn't really be passing the request.url here as it's the wrong url to be testing (it's the renderer URL)
    //* But when running tests we need this to be a real URL or we get errors.
    const isTestMode = testModeIsOn(new URL(searchParams.get('origin_url') || request.url))

    flowId = forcedJson?.flowId || path[0]
    const flowLogger = childLogger2?.child({ flow_id: flowId })

    const embeddableDraftId = searchParams.get('embeddable_draft')
    const liveParam = searchParams.get('live')
    const live = liveParam === 'refresh' || liveParam === 'rerender' ? liveParam : undefined
    //* If i'm on live mode and using drafts I cannot use the forcedJson I need to get it from the supabase storage draft
    if (embeddableDraftId && live === 'rerender') forcedJson = undefined

    const {
      flow: parsedFlowOriginal,
      groupId,
      projectId,
      version,
      metadata,
    } = await getFlow(context, flowId, versionQuery, forcedJson, embeddableDraftId, branchId)

    const parsedFlow: Flow = removeHideInitially(parsedFlowOriginal)

    //* Extract user data from cookies if enabled
    const userDataFromCookies =
      (parsedFlow.store_experiments_in_cookies &&
        parseUserDataFromCookies(request.headers, flowId)) ||
      {}

    //* Extract user data from search params
    const providedUserData: { [key: string]: string } = { ...userDataFromCookies }
    const excludedKeys = new Set(['id', 'version'])
    for (let [key, value] of searchParams) {
      if (!excludedKeys.has(key)) providedUserData[key] = value
    }

    //* We initilize the userData, but maintaing a difference between including defaults and not including them
    //* For generating the body we will use the userData with defaults, but the response will be the one without defaults
    const {
      flow,
      data: userDataBeforeExternalContent,
      userDataWithoutDefaults: userDataBeforeExternalContentWithoutDefaults,
    } = context.isServerSide
      ? await asyncStripVariantConditions({
          context,
          baseFlow: parsedFlow,
          baseUserData: providedUserData,
          projectId,
          searchParams,
        })
      : stripVariantConditions({
          context,
          baseFlow: parsedFlow,
          baseUserData: providedUserData,
          searchParams,
        })

    flowLogger?.info({
      scope: 'Handle request',
      functionName: 'handleRequest',
      message: 'Initial set up of user data',
      props: {
        providedUserData,
        userDataBeforeExternalContent,
        userDataBeforeExternalContentWithoutDefaults,
      },
    })

    // Path length must equal one to match the first request. After that, others happen,
    // for example to load pages or components, this prevents external data from being loaded unexpectedly.
    const shouldLoadExternalContent = Boolean(
      path.length === 1 && path[0] !== 'css' && flow.content_sources
    )
    flowLogger?.info({
      scope: 'Handle request',
      functionName: 'handleRequest',
      message: 'Should load external content',
      props: {
        path,
        shouldLoadExternalContent,
      },
    })
    const externalContentUrlToTest =
      (request.headers && request.headers.get('X-Origin-URL')) ||
      searchParams.get('origin_url') ||
      request.url.href
    const timeExternalContent = Date.now()
    const externalContent = shouldLoadExternalContent
      ? await loadExternalContent(context, flow, metadata, externalContentUrlToTest, isTestMode)
      : {}

    if (shouldLoadExternalContent) {
      flowLogger?.info({
        scope: 'Handle request',
        functionName: 'handleRequest',
        message: 'External content loaded',
        props: {
          took: Date.now() - timeExternalContent,
        },
      })
    }

    const userDataWithoutDefaults = {
      ...userDataBeforeExternalContentWithoutDefaults,
      ...userDataFromCookies,
      ...externalContent,
    }
    const userData = {
      ...userDataBeforeExternalContent,
      ...externalContent,
    }

    const ipAddress = request?.headers?.get('x-real-ip')
    if (ipAddress) {
      userData.ip_address = ipAddress
      userDataWithoutDefaults.ip_address = ipAddress
    }

    const pageContentOnly = Boolean(path[1] === 'page' && path[2])
    const componentContentOnly = Boolean(path[3] === 'component' && path[4])

    // console.log('componentContentOnly, pageContentOnly', componentContentOnly, pageContentOnly)
    // console.log('forcedJson', forcedJson)
    // console.log('request.body.json', request.body?.json)

    const parsedFlowPages = parsedFlow.pages || []

    // console.log(
    //   'context, flowId, versionQuery, forcedJson',
    //   context,
    //   flowId,
    //   versionQuery,
    //   forcedJson
    // )
    // console.log('parsedFlow', parsedFlow)
    // console.log('flow', flow)
    // console.log('parsedFlowPages', parsedFlowPages)

    let pageId = searchParams.get('pageId') || (path[1] === 'page' && path[2])
    // console.log('pageId', pageId)
    if (searchParams.get('pageKey')) {
      const foundPageId = parsedFlowPages.find((p) => p.key === searchParams.get('pageKey'))?.id
      if (foundPageId) pageId = foundPageId
    } else if (searchParams.get('pageIndex')) {
      try {
        const foundPageId = parsedFlowPages[parseInt(searchParams.get('pageIndex') || '0')]?.id
        if (foundPageId) pageId = foundPageId
      } catch (error) {
        console.warn(`Ignored non-numerical pageIndex "${searchParams.get('pageIndex')}"`, error)
      }
    }
    /* 
    Priority:
    The specified page id of the raw flow
    The first non hidden page id of the filtered flow (filtered by experiments)
    The first page of the filtered flow 
    The first page of the raw flow [Should never hit this case in most situations]
    An empty object to ensure the rest of the functions do not crash.
     */
    const page =
      parsedFlowPages.find((p) => p.id === pageId) ||
      flow.pages.filter((p) => !p.hide && !p.__hide_initially)[0] ||
      flow.pages[0] ||
      parsedFlowPages[0] ||
      {}

    if (page.id) {
      userData.current_page_id = page.id
      userDataWithoutDefaults.current_page_id = page.id
    }
    // if (searchParams.get('pageKey') && page.id) userData.current_page_id = page.id

    const globalLocation = path.includes('global') ? 'true' : null

    const libraryPageId = searchParams.get('library_page_id')
    const libraryComponentId = searchParams.get('library_component_id')

    // console.log('libraryPageId', libraryPageId)
    // console.log('libraryComponentId', libraryComponentId)

    const components = libraryComponentId
      ? await getLibraryComponent(context, libraryComponentId)
      : null

    const popup = searchParams.get('popup') === 'true'

    const editingMode = isBuilderEditingMode(request.url)
    const liteEditingMode =
      searchParams.get('builder_lite') === 'true' || searchParams.get('builder-lite') === 'true'
    const allowJsonUpdate =
      searchParams.get('allow_json_update') === 'true' || searchParams.get('live') === 'rerender'

    const css = path[1] === 'css' || !pageContentOnly ? stylesGenerator(flow, flowId, popup) : ''
    const static_render = searchParams.get('static_render') === 'true'
    const htmlOnly = searchParams.get('html_only') === 'true'
    const repeatables = forcedJson?.repeatables

    const stripePublishableKey = context.environment
      ? await getStripeKey(context, projectId, isTestMode ? 'dev' : 'prod', 'public_key').catch(
          () => undefined
        )
      : undefined

    const body = generateBodyHtml(
      {
        origin,
        flowId,
        groupId,
        projectId,
        globalLocation,
        version,
        css,
        userData,
        popup,
        editingMode,
        liteEditingMode,
        allowJsonUpdate,
        static_render,
        repeatables,
        branchId,
        logger: context.logger,
      },
      pageContentOnly,
      componentContentOnly,
      flow,
      forcedJson?.page || page,
      components,
      forcedJson?.component || undefined,
      stripePublishableKey
    )

    flow.pages?.forEach((p) => delete p.__hide_initially)
    flow.pages?.forEach((p) => p.components?.forEach((c) => delete c.__hide_initially))
    flow.components?.forEach((c) => delete c.__hide_initially)

    const html = path[1] === 'css' ? css : body

    if (htmlOnly) {
      flowLogger?.info({
        scope: 'Handle request',
        functionName: 'handleRequest',
        message: 'HTML only',
        props: {
          took: Date.now() - timeHandleRequest,
        },
      })
      const wrappedHtml = `<!DOCTYPE html>
      <html>
      <style>
        html, body {
          margin: 0;
        }
      </style>
      ${html}
      </html>`
      return { responseData: wrappedHtml, contentType: 'text/html;charset=UTF-8' }
    }

    if (path[1] === 'css' || pageContentOnly) {
      const contentType = `text/${path[0] === 'css' ? 'css' : 'html'};charset=UTF-8`
      // console.log(`📩 Response (${context.env})`, 200, (html && html.slice(0, 100) + '...') || html)
      flowLogger?.info({
        scope: 'Handle request',
        functionName: 'handleRequest',
        message: `Handled request (${path[0] === 'css' ? 'css' : 'page'})`,
        props: {
          took: Date.now() - timeHandleRequest,
        },
      })
      return { responseData: html, contentType }
    }
    const js = getJs(flow, groupId, metadata || {}, userDataWithoutDefaults)
    const clientSideJs = getClientSideJs(flow, groupId, metadata || {}, userDataWithoutDefaults)
    const clientSideJsV3 = getClientSideJsV3(
      context,
      flow,
      groupId,
      metadata || {},
      userDataWithoutDefaults
    )
    const jsonResponse = JSON.stringify({
      html,
      id: flow.id,
      version: version,
      flow,
      userData: userDataWithoutDefaults,
      js,
      client_side_js: clientSideJs,
      client_side_js_v3: clientSideJsV3,
      group_id: groupId,
      metadata,
    })

    flowLogger?.info({
      scope: 'Handle request',
      functionName: 'handleRequest',
      message: 'Handled request',
      props: {
        took: Date.now() - timeHandleRequest,
        metadata,
      },
    })
    // console.log(`📩 Response (${context.env})`, 200, JSON.parse(jsonResponse))
    return { responseData: jsonResponse }
  } catch (error) {
    logger?.error({
      scope: 'Handle request',
      functionName: 'handleRequest',
      message: 'Error in handleRequest',
      props: {
        flow_id: flowId,
        error: error ? { error } : { _no_error: true },
      },
    })
    console.log(`📛 Response Error (${context.env})`, error)
    console.error('error:', error)
    return { responseData: error }
    // return new Error('Something went weirdly quite wrong.')
  }
}

type FirebaseVersionResponse = {
  documents: FirestoreVersion[]
  nextPageToken?: string
}

type FirestoreVersion = {
  name: string
  fields: {
    form: { stringValue: string }
    version: { integerValue: string }
    author: {
      mapValue: { fields: { userId: { stringValue: string }; userName: { stringValue: string } } }
    }
    updatedAt: { timestampValue: string }
    groupId: { stringValue: string }
    from_version: { integerValue: string }
    status: { stringValue: string }
    saved: { booleanValue: boolean }
    json_storage_location?: { stringValue: string }
  }
  createTime: string
  updateTime: string
}

type FlowData = {
  flow: Flow
  groupId: string
  projectId: string
  version: number
  metadata?: FlowMetadata | {}
}

async function getFlow(
  context: Context,
  flowId: string,
  version: string | null,
  forcedJson?: { flow: Flow; flowId: string; groupId: string; projectId: string },
  embeddableDraftId?: string | null,
  branchId?: string | null
): Promise<FlowData> {
  const timeGetFlow = Date.now()
  if (forcedJson?.flow && forcedJson?.groupId) {
    return {
      flow: forcedJson.flow,
      groupId: forcedJson.groupId,
      projectId: forcedJson.projectId,
      version: +(version || 0),
    }
  }

  const versionString =
    version === 'latest' ? await getLatestVersionNumber({ context, flowId, branchId }) : version

  const timeGetFlowAllSettled = Date.now()

  let draft: any = null
  if (embeddableDraftId) {
    try {
      draft = await getDrafts(flowId, embeddableDraftId)
    } catch (error) {
      console.log('No drafts recovered', error)
    }
  }

  const getJsonPromise =
    draft && !draft.error
      ? Promise.resolve(draft)
      : getFromKv(
          context,
          'flowsBucket',
          versionString ? `${flowId}@${branchId ? `${branchId}@` : ''}${versionString}` : flowId
        )

  const [kvFlowJsonStringRes, kvFlowMetadataRes] = await Promise.allSettled([
    getJsonPromise,
    getFromKv(context, 'flowsMetadataBucket', flowId),
  ])

  context.logger?.info({
    scope: 'Handle request',
    functionName: 'getFlow',
    message: 'Get Flow AllSettled',
    props: {
      flow_id: flowId,
      took: Date.now() - timeGetFlowAllSettled,
    },
  })

  const kvFlowJsonPromiseSuccedded = Boolean(
    kvFlowJsonStringRes.status === 'fulfilled' && kvFlowJsonStringRes?.value
  )

  //* If kv json failed to fetch, we try to fetch it from supabase storage
  const supabaseJsonRes = kvFlowJsonPromiseSuccedded
    ? null
    : await getFromSupabaseStorage(
        `${flowId}/${branchId ? `${branchId}/` : ''}version-${versionString}`
      )

  const supabaseJson: Flow | null = !supabaseJsonRes ? null : await supabaseJsonRes.json()

  //* If kv metadata failed to fetch, or supabase json also failed, we try to fetch it from firestore (old savvy-import-testing no longer updated with new data)
  const firestoreDataRes =
    kvFlowMetadataRes.status === 'fulfilled' &&
    kvFlowMetadataRes.value?.group_id &&
    (kvFlowJsonPromiseSuccedded || (!kvFlowJsonPromiseSuccedded && supabaseJson && supabaseJson.id))
      ? null
      : await Promise.allSettled([getFromFirestore(context, flowId, versionString)])

  if (firestoreDataRes) {
    context.logger?.info({
      scope: 'Handle request',
      functionName: 'getFlow',
      message: 'Used Firestore for group_id and/or flowString',
      props: {
        flow_id: flowId,
        used_for_group_id: kvFlowMetadataRes.status !== 'fulfilled',
        used_for_flowString: kvFlowJsonStringRes.status !== 'fulfilled',
      },
    })
  }

  //* The JSON is define first by kv, then by supabase, then by firestore
  const flowJson: Flow = (kvFlowJsonStringRes.status === 'fulfilled' &&
    kvFlowJsonStringRes?.value) ||
    (supabaseJson && supabaseJson.id && supabaseJson) ||
    JSON.parse(
      (firestoreDataRes?.[0].status === 'fulfilled' && firestoreDataRes?.[0]?.value?.flowString) ||
        'null'
    ) || { pages: [] }

  const metadata: FlowMetadata = {
    id: flowId,
    title:
      (kvFlowMetadataRes.status === 'fulfilled' && (kvFlowMetadataRes.value?.title as string)) ||
      flowJson.title ||
      '', // firestoreDataRes.title,
    group_id:
      (kvFlowMetadataRes.status === 'fulfilled' && (kvFlowMetadataRes.value?.group_id as string)) ||
      (firestoreDataRes?.[0].status === 'fulfilled' && firestoreDataRes?.[0]?.value?.groupId) ||
      '',
    project_id:
      (kvFlowMetadataRes.status === 'fulfilled' &&
        (kvFlowMetadataRes.value?.project_id as string)) ||
      '',
    prod_version:
      (kvFlowMetadataRes.status === 'fulfilled' &&
        ((kvFlowMetadataRes.value?.prod_version as number) ||
          (kvFlowMetadataRes.value?.live_version as number))) ||
      0,
    staging_version:
      (kvFlowMetadataRes.status === 'fulfilled' &&
        (kvFlowMetadataRes.value?.staging_version as number)) ||
      0,
  }

  if (
    !['dev', 'ci', 'test', 'local'].includes(context.env) &&
    !(kvFlowMetadataRes.status === 'fulfilled' && kvFlowMetadataRes.value?.group_id) &&
    firestoreDataRes?.[0].status === 'fulfilled' &&
    firestoreDataRes?.[0]?.value?.groupId
  ) {
    context.logger?.error({
      scope: 'Handle request',
      functionName: 'getFlow',
      message: 'Migration Issue: Renderer used Firestore data for group_id',
      props: {
        env: context.env,
        flow_id: flowId,
        version,
        ...metadata,
      },
    })
    notify(context, 'Used Firestore data for group_id', MIGRATION_ISSUES_CHANNEL_ID, {
      env: context.env,
      flow_id: flowId,
      version,
      ...metadata,
    })
  }

  // console.log('flowJson', JSON.stringify(flowJson).slice(0, 50))
  // console.log('metadata', metadata)

  const versionNumber =
    (versionString === 'staging'
      ? metadata.staging_version
      : versionString
      ? parseInt(versionString || '0')
      : metadata.prod_version) || 0

  const returnVal = {
    flow: flowJson,
    groupId: metadata.group_id,
    projectId: metadata.project_id,
    version: versionNumber,
    branchId,
    metadata,
  }

  context.logger?.info({
    scope: 'Handle request',
    functionName: 'getFlow',
    message: 'Get Flow Returned',
    props: {
      flow_id: flowId,
      took: Date.now() - timeGetFlow,
    },
  })
  // console.log('returnVal', { ...returnVal, flow: JSON.stringify(returnVal.flow).slice(0, 50) })
  return returnVal
}

// @TODO: same for getLatestVersion()

async function getLatestVersionNumber({
  context,
  flowId,
  branchId,
}: {
  context: Context
  flowId: string
  branchId?: string | null
}): Promise<string> {
  const timeLatestVersion = Date.now()
  const branchFilter = branchId ? `branch_id=eq.${branchId}` : 'branch_id=is.null'
  const supabaseUrl = `${SUPABASE_DATABASE_URL}/rest/v1/public_flow_versions?select=version_number&flow_id=eq.${flowId}&${branchFilter}&status=neq.DRAFT&order=version_number.desc&limit=1`

  const headers = {
    apikey: SUPABASE_PUBLIC_KEY,
    Authorization: `Bearer ${SUPABASE_PUBLIC_KEY}`,
    'Content-Type': 'application/json',
  }

  const data = await context
    .fetch(supabaseUrl, {
      headers,
    })
    .then((res: Response) => res.json())
    .catch((error: Error) => {
      throw new Error(`Failed to fetch latest version from Supabase: ${error.message}`)
    })

  context.logger?.info({
    scope: 'Handle request',
    functionName: 'getLatestVersionNumber',
    message: 'Get latest version',
    props: {
      flow_id: flowId,
      took: Date.now() - timeLatestVersion,
    },
  })

  const getLatestVersion: string = `${data[0]?.version_number || 0}`
  return getLatestVersion
}

async function getLibraryComponent(context: Context, id: string) {
  const timeLibraryComponent = Date.now()
  const url = `https://firestore.googleapis.com/v1/projects/savvy-import-testing/databases/(default)/documents/library/${id}`
  const res = await context.fetch(url)
  const data: FirebaseResponseLibraryItem = await res.json()

  context.logger?.info({
    scope: 'Handle request',
    functionName: 'getLibraryComponent',
    message: 'Get library component',
    props: {
      took: Date.now() - timeLibraryComponent,
      library_id: id,
    },
  })
  return JSON.parse(data.fields.body.stringValue || '{}')
}

async function getFromFirestore(
  context: Context,
  flowId: string,
  version: string | null
): Promise<{
  flowString: string
  groupId: string
  version: number
}> {
  const timeFS = Date.now()
  const baseUrl = `https://firestore.googleapis.com/v1/projects/savvy-import-testing/databases/(default)/documents/forms/${flowId}`
  const fetchUrl = version ? `${baseUrl}/history/version-${version}` : baseUrl

  const res = await context.fetch(fetchUrl)
  const data: FirebaseResponseFlow = await res.json()

  context.logger?.info({
    scope: 'Handle request',
    functionName: 'getFromFirestore',
    message: 'Firestore read',
    props: {
      flow_id: flowId,
      took: Date.now() - timeFS,
      result_string: (data.fields.form.stringValue || '').slice(0, 200),
    },
  })

  return {
    flowString: data.fields.form.stringValue,
    groupId: data.fields?.groupId?.stringValue,
    version: +(data.fields?.version?.integerValue || 0),
  }
}

async function getFromKv(
  context: Context,
  kvKey: string,
  key: string
): Promise<Record<string, unknown>> {
  const timeKv = Date.now()
  const baseKvUrl =
    kvKey === 'flowsBucket'
      ? `https://flow-storage.heysavvy.workers.dev/flows`
      : kvKey === 'flowsMetadataBucket'
      ? `https://flow-metadata-storage.heysavvy.workers.dev/flows`
      : null
  if (!baseKvUrl) throw new Error(`Bucket "${kvKey}" not supported`)
  const kvUrl = `${baseKvUrl}/${key}`
  const attemptToUseCache = context.request?.url.searchParams.get('use_cache') && !key.includes('@')
  const cacheTimeout = context.request?.url.searchParams.get('cache_timeout') || 10
  // const refreshCache = context.request?.url.searchParams.get('refresh_cache')
  const cache = (caches as any)?.default
  const useCache = Boolean(attemptToUseCache && context.ctx && cache?.match)

  context.logger?.info({
    scope: 'Handle request',
    functionName: 'getFromKv',
    message: 'cache context.ctx' + ` (${kvKey})`,
    props: { context_ctx: context.ctx },
  })
  context.logger?.info({
    scope: 'Handle request',
    functionName: 'getFromKv',
    message: 'cache caches' + ` (${kvKey})`,
    props: { caches },
  })
  context.logger?.info({
    scope: 'Handle request',
    functionName: 'getFromKv',
    message: `cache useCache: ${useCache}` + ` (${kvKey})`,
    props: { useCache },
  })

  if (useCache) {
    try {
      context.logger?.info({
        scope: 'Handle request',
        functionName: 'getFromKv',
        message: 'kvUrl',
        props: { kvUrl },
      })
      const cachedResponse = await cache.match(kvUrl)
      const cachedValue = cachedResponse && (await cachedResponse.json())
      if (kvKey === 'flowsMetadataBucket')
        context.logger?.info({
          scope: 'Handle request',
          functionName: 'getFromKv',
          message: 'cachedValue',
          props: { cachedValue },
        })
      if (cachedValue) {
        context.logger?.info({
          scope: 'Handle request',
          functionName: 'getFromKv',
          message: 'KV read (cached)' + ` (${kvKey})`,
          props: {
            took: Date.now() - timeKv,
            kv_key: kvKey,
            key,
            result_string: JSON.stringify(cachedValue).slice(0, 200),
            retrieved_with_cache: true,
          },
        })
        return cachedValue
      }
    } catch (error) {
      console.error('cache error', error)
    }
  }

  const kv = (context as any)[kvKey]

  const res = await (kv && kv.get?.(key))

  if (kv && res) {
    if (res) {
      context.logger?.info({
        scope: 'Handle request',
        functionName: 'getFromKv',
        message: 'KV read' + ` (${kvKey})`,
        props: {
          took: Date.now() - timeKv,
          kv_key: kvKey,
          key,
          result_string: JSON.stringify(res || '{}').slice(0, 200),
        },
      })
      if (attemptToUseCache) {
        const responseToCache = new Response(res, {
          headers: {
            'content-type': 'application/json;charset=UTF-8',
            'Cache-Control': `s-maxage=${cacheTimeout}`,
          },
        })
        context.logger?.info({
          scope: 'Handle request',
          functionName: 'getFromKv',
          message: 'Setting to cache 1' + ` (${kvKey})`,
          props: { kv_key: kvKey },
        })
        if (kvKey === 'flowsMetadataBucket') {
          context.logger?.info({
            scope: 'Handle request',
            functionName: 'getFromKv',
            message: 'resultString' + ` (${kvKey})`,
            props: { res },
          })
        }
        context.ctx?.waitUntil(
          cache.put(kvUrl, responseToCache).then(() =>
            context.logger?.info({
              scope: 'Handle request',
              functionName: 'getFromKv',
              message: 'KV cached' + ` (${kvKey})`,
              props: { kv_key: kvKey },
            })
          )
        )
      } else {
        context.logger?.info({
          scope: 'Handle request',
          functionName: 'getFromKv',
          message: 'KV read failed',
          props: {
            took: Date.now() - timeKv,
            kv_key: kvKey,
            key,
            result_string: JSON.stringify(res || '{}').slice(0, 200),
          },
        })
      }
    }
    return JSON.parse(res)
  } else {
    // console.log('baseKvUrl', baseKvUrl)
    const headers = {
      'Content-Type': 'application/json',
    }

    const resultString = await fetch(kvUrl, { headers }).then((res) => {
      return res.text()
    })

    if (attemptToUseCache) {
      const responseToCache = new Response(resultString, {
        headers: {
          'content-type': 'application/json;charset=UTF-8',
          'Cache-Control': `s-maxage=${cacheTimeout}`,
        },
      })
      context.logger?.info({
        scope: 'Handle request',
        functionName: 'getFromKv',
        message: 'Setting to cache 2' + ` (${kvKey})`,
        props: { kv_key: kvKey },
      })
      if (kvKey === 'flowsMetadataBucket') {
        context.logger?.info({
          scope: 'Handle request',
          functionName: 'getFromKv',
          message: 'resultString' + ` (${kvKey})`,
          props: { resultString },
        })
      }
      context.ctx?.waitUntil(
        cache.put(kvUrl, responseToCache).then(() =>
          context.logger?.info({
            scope: 'Handle request',
            functionName: 'getFromKv',
            message: 'KV cached' + ` (${kvKey})`,
            props: { kv_key: kvKey },
          })
        )
      )
    }

    context.logger?.info({
      scope: 'Handle request',
      functionName: 'getFromKv',
      message: 'KV read (via fetch)' + ` (${kvKey})`,
      props: {
        took: Date.now() - timeKv,
        kv_key: kvKey,
        key,
        result_string: (resultString || '').slice(0, 200),
      },
    })

    return JSON.parse(resultString)
  }
}

function removeHideInitially(obj: any): any {
  if (obj && typeof obj === 'object') {
    if (obj.pages && Array.isArray(obj.pages)) {
      obj.pages.forEach((page: any) => {
        if (page.components && Array.isArray(page.components)) {
          page.components.forEach((component: any) => {
            if (component && typeof component === 'object' && component.__hide_initially) {
              delete component.__hide_initially
            }
          })
        }
        if (page.__hide_initially) {
          delete page.__hide_initially
        }
      })
    }
    if (obj.components && Array.isArray(obj.components)) {
      obj.components.forEach((component: any) => {
        if (component && typeof component === 'object' && component.__hide_initially) {
          delete component.__hide_initially
        }
      })
    }
  } else {
    console.error(`Error, flow JSON is not an object. typeof flow JSON: ${typeof obj}`, obj)
    return obj
  }
  return obj
}

async function getDrafts(flowId: string, embeddableDraftId?: string | null) {
  if (!flowId || !embeddableDraftId) return null

  const res = await getFromSupabaseStorage(`${flowId}/drafts/${embeddableDraftId}`)
  const data = await res.json()

  return data
}

function getFromSupabaseStorage(path: string) {
  const bucketName = 'flows'
  return fetch(
    `${SUPABASE_DATABASE_URL}/storage/v1/object/${bucketName}/${path}?version=${Date.now()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_PUBLIC_KEY}`,
        apikey: SUPABASE_PUBLIC_KEY,
      },
    }
  )
}

export function parseUserDataFromCookies(
  headers: Headers | null,
  flowId: string
): { [key: string]: string } | null {
  if (!headers) return null

  const cookieHeader = headers.get('Cookie')
  if (!cookieHeader) return null

  // Parse cookies from the Cookie header
  const cookies: { [key: string]: string } = {}
  cookieHeader.split(';').forEach((cookie: string) => {
    const [key, value] = cookie.trim().split('=')
    if (key && value) {
      cookies[key] = value
    }
  })

  // Look for the experiment cookie for this flow
  const cookieKey = `Embeddables.${flowId}`
  const userDataCookie = cookies[cookieKey]

  if (!userDataCookie) return null

  try {
    // Detect if the cookie is a valid JSON string
    if (!isValidJsonString(decodeURIComponent(userDataCookie))) {
      return null
    }

    // Parse the JSON data from the cookie
    const userData = JSON.parse(decodeURIComponent(userDataCookie))

    // Only return keys that start with 'split_' (experiment keys)
    const filteredData: { [key: string]: string } = {}
    Object.keys(userData).forEach((key) => {
      if (key.startsWith('split_')) {
        filteredData[key] = userData[key]
      }
    })

    return Object.keys(filteredData).length > 0 ? filteredData : null
  } catch (error) {
    console.error('Error parsing experiment cookie:', error)
    return null
  }
}

function isValidJsonString(str: string): boolean {
  try {
    !str || JSON.parse(str)
    return true
  } catch (error) {
    console.warn('Invalid JSON string:', str)
    return false
  }
}
